import { useEffect, useState } from 'preact/hooks';
import { BASIS_UNITS, basisTotal, kr, lineNet, VAT_RATES, type BasisLine, type Billing, type FinanceInvoice } from '@kff/domain';
import { ApiError, type Api } from '../api.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Dialog, Pill, toast } from '../ui.js';
import { ClientTermsBlock, quickLines, type ClientCostsView } from './terms.js';
import { TravelHints, TravelRow, TripDialog, tripLabel, type TravelHint, type TravelView } from './travel.js';

/**
 * Invoicing on the client card and the Finance page (owner 2026-10-29, analys/bokio-fakturering-2026-10-29/forslag.md):
 * the invoice details (the same in Bokio both ways), the month's invoice basis the producer confirms and an admin approves
 * into a draft in Bokio, and the client's invoices as Bokio has them. The Hub never sends, books or credits an invoice.
 */

export type BasisStatus = 'open' | 'confirmed' | 'approved' | 'in_bokio' | 'skipped';
export interface InvoiceRow extends FinanceInvoice {
  currency: string;
  basisId: string | null;
}
export interface BasisView {
  id: string;
  clientId: string;
  period: string;
  status: BasisStatus;
  lines: BasisLine[];
  note: string | null;
  confirmedAt: string | null;
  approvedAt: string | null;
  bokioInvoiceId: string | null;
  /** set when the Hub made the draft in Bokio; without it an in_bokio basis was marked as invoiced straight in Bokio */
  pushedAt?: string | null;
  /** invoices made straight in Bokio for the client in the month: already invoiced */
  elsewhere?: { number: string | null; status: string; net: number }[];
  bokioError: string | null;
  rev: number;
  client: { id: string; name: string; code: string };
  /** the further company in Bokio it is invoiced to; null: the client's main company */
  company?: { id: string; name: string; orgNo: string | null } | null;
  total: { net: number; vat: number; gross: number; unpriced: number; lines: number };
  missing: string[];
  linked: boolean;
  invoice: InvoiceRow | null;
  monthLabel: string;
  travel: TravelView;
  costs: ClientCostsView;
}
interface Conflict {
  at: string;
  bokio: Billing & { orgNo: string | null };
  fields: string[];
}
interface BillingView {
  orgNo: string | null;
  billing: Billing;
  suggestion: Partial<Billing> | null;
  mondayText: { invoiceInfo: string | null; address: string | null } | null;
  missing: string[];
  bokio: { linked: boolean; companies?: { id: string; name: string; orgNo: string | null }[]; syncedAt: string | null; conflict: Conflict | null; on: boolean };
  invoices: InvoiceRow[];
  basis: BasisView | null;
  /** the main company's basis first, then one per further company with projects that month */
  bases?: BasisView[];
  travel: TravelView;
  canEdit: boolean;
  canConfirm: boolean;
  canApprove: boolean;
  pushError?: string | null;
}

export const TX = texts(
  {
    head: 'Invoicing',
    details: 'Invoice details',
    edit: 'Change',
    add: 'Add invoice details',
    noDetails: 'No invoice details yet.',
    missing: (f: string) => `Missing before invoicing: ${f}`,
    elsewhere: (n: string) => `Already invoiced in Bokio this month (${n}). Approve only if this is invoiced as well.`,
    fields: { orgNo: 'Org no', vatNo: 'VAT no', line1: 'Address', line2: 'Address line 2', postalCode: 'Postcode', city: 'Town', country: 'Country', email: 'Invoice email', paymentTerms: 'Payment terms (days)', reference: 'Their reference', poNumber: 'PO / order no' } as Record<string, string>,
    refHint: 'Reference and PO go on every invoice.',
    linked: 'Linked to Bokio',
    alsoCompanies: (n: string) => `Also invoiced as: ${n}`,
    linkedSynced: (d: string) => `Linked to Bokio · synced ${d}`,
    notLinked: 'Not linked to a customer in Bokio yet (Finance › Customers in Bokio).',
    fromMonday: 'Monday had this:',
    useSuggestion: 'Fill in from Monday',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Invoice details saved',
    savedBokio: 'Invoice details saved, also in Bokio',
    pushFailed: (m: string) => `Saved in the Hub, but Bokio said: ${m}`,
    conflict: 'Changed both here and in Bokio',
    conflictBody: (f: string) => `Differs: ${f}. Which version is right?`,
    keepHub: 'Keep the Hub’s',
    takeBokio: 'Take Bokio’s',
    hubSide: 'Hub',
    bokioSide: 'Bokio',
    invoices: 'Invoices',
    noInvoices: 'No invoices in Bokio yet.',
    due: (d: string) => `due ${d}`,
    basis: (m: string) => `To invoice ${m}`,
    open: 'Review the basis',
    lines: (n: number) => `${n} ${n === 1 ? 'line' : 'lines'}`,
    exVat: 'excl. VAT',
    status: { open: 'To confirm', confirmed: 'Confirmed', approved: 'Approved', in_bokio: 'Draft in Bokio', skipped: 'Not invoiced' } as Record<BasisStatus, string>,
    inv: { draft: 'Draft', published: 'Sent', paid: 'Paid', overPaid: 'Overpaid', underPaid: 'Part paid', overdue: 'Overdue', credited: 'Credited', credit: 'Credit note' } as Record<string, string>,
    // the editor
    basisTitle: (c: string, m: string) => `${c} · ${m}`,
    basisSub: 'Built from the agreement and the month’s projects. Change what is wrong, add what is missing, then confirm.',
    text: 'Description',
    qty: 'Qty',
    unit: 'Unit',
    units: { month: 'month', production: 'production', once: 'once', h: 'hours', piece: 'pcs' } as Record<string, string>,
    price: 'Unit price',
    vat: 'VAT',
    sum: 'Amount',
    noPrice: 'No price',
    fromAgreement: 'From the agreement',
    fromProject: 'From the project',
    fromMondayDeal: 'From the Monday agreement',
    manual: 'Added',
    trip: 'Travel',
    changed: 'changed',
    remove: 'Remove',
    restore: 'Restore',
    addLine: 'Add line',
    note: 'Note to whoever invoices',
    total: 'Total excl. VAT',
    totalVat: (v: string, g: string) => `VAT ${v} · ${g} incl. VAT`,
    saveLines: 'Save',
    confirm: 'Confirm',
    confirmed: 'Confirmed. An admin approves it into Bokio.',
    reopen: 'Open again',
    skip: 'Not invoicing this month',
    approve: 'Approve and make a draft in Bokio',
    approved: 'Draft made in Bokio with the basis attached',
    inBokio: (n: string | null) => (n ? `In Bokio as invoice ${n}` : 'In Bokio as a draft'),
    invoicedThere: 'Invoiced in Bokio',
    markInvoiced: 'Already invoiced in Bokio…',
    pickInvoices: 'Which invoices in Bokio cover this month? They can be from another month.',
    noChoices: 'No invoice for this client in Bokio two months either side. Link the client under Finance › Customers in Bokio if it is missing.',
    saveInvoiced: 'Mark as invoiced',
    markedOk: 'Marked as invoiced',
    bokioError: (m: string) => `Bokio: ${m}`,
    stale: 'Someone else changed the basis. It is reloaded; make your change again.',
    locked: 'Confirmed. Open it again to change it.',
    empty: 'Nothing to invoice this month yet.'
  },
  {
    head: 'Fakturering',
    details: 'Fakturauppgifter',
    edit: 'Ändra',
    add: 'Lägg till fakturauppgifter',
    noDetails: 'Inga fakturauppgifter än.',
    missing: (f: string) => `Saknas innan fakturering: ${f}`,
    elsewhere: (n: string) => `Redan fakturerad i Bokio denna månad (${n}). Godkänn bara om även detta ska faktureras.`,
    fields: { orgNo: 'Org.nr', vatNo: 'Momsreg.nr', line1: 'Adress', line2: 'Adressrad 2', postalCode: 'Postnummer', city: 'Ort', country: 'Land', email: 'Fakturamejl', paymentTerms: 'Betalningsvillkor (dagar)', reference: 'Er referens', poNumber: 'PO / ordernummer' },
    refHint: 'Referens och PO hamnar på varje faktura.',
    linked: 'Kopplad till Bokio',
    alsoCompanies: (n: string) => `Faktureras även som: ${n}`,
    linkedSynced: (d: string) => `Kopplad till Bokio · synkad ${d}`,
    notLinked: 'Inte kopplad till en kund i Bokio än (Ekonomi › Kunder i Bokio).',
    fromMonday: 'Monday hade det här:',
    useSuggestion: 'Fyll i från Monday',
    save: 'Spara',
    cancel: 'Avbryt',
    saved: 'Fakturauppgifterna är sparade',
    savedBokio: 'Fakturauppgifterna är sparade, även i Bokio',
    pushFailed: (m: string) => `Sparat i Hubben, men Bokio svarade: ${m}`,
    conflict: 'Ändrat både här och i Bokio',
    conflictBody: (f: string) => `Skiljer sig: ${f}. Vilken version stämmer?`,
    keepHub: 'Behåll Hubbens',
    takeBokio: 'Ta Bokios',
    hubSide: 'Hubben',
    bokioSide: 'Bokio',
    invoices: 'Fakturor',
    noInvoices: 'Inga fakturor i Bokio än.',
    due: (d: string) => `förfaller ${d}`,
    basis: (m: string) => `Att fakturera ${m}`,
    open: 'Granska underlaget',
    lines: (n: number) => `${n} ${n === 1 ? 'rad' : 'rader'}`,
    exVat: 'exkl. moms',
    status: { open: 'Att bekräfta', confirmed: 'Bekräftat', approved: 'Godkänt', in_bokio: 'Utkast i Bokio', skipped: 'Faktureras inte' },
    inv: { draft: 'Utkast', published: 'Skickad', paid: 'Betald', overPaid: 'Överbetald', underPaid: 'Delbetald', overdue: 'Förfallen', credited: 'Krediterad', credit: 'Kreditfaktura' },
    basisTitle: (c: string, m: string) => `${c} · ${m}`,
    basisSub: 'Byggt från avtalet och månadens projekt. Ändra det som är fel, lägg till det som saknas och bekräfta.',
    text: 'Beskrivning',
    qty: 'Antal',
    unit: 'Enhet',
    units: { month: 'mån', production: 'produktion', once: 'engång', h: 'tim', piece: 'st' },
    price: 'À-pris',
    vat: 'Moms',
    sum: 'Belopp',
    noPrice: 'Pris saknas',
    fromAgreement: 'Från avtalet',
    fromProject: 'Från projektet',
    fromMondayDeal: 'Från Monday-avtalet',
    manual: 'Tillagd',
    trip: 'Resa',
    changed: 'ändrad',
    remove: 'Ta bort',
    restore: 'Lägg tillbaka',
    addLine: 'Lägg till rad',
    note: 'Anteckning till den som fakturerar',
    total: 'Summa exkl. moms',
    totalVat: (v: string, g: string) => `Moms ${v} · ${g} inkl. moms`,
    saveLines: 'Spara',
    confirm: 'Bekräfta',
    confirmed: 'Bekräftat. En admin godkänner det till Bokio.',
    reopen: 'Öppna igen',
    skip: 'Faktureras inte denna månad',
    approve: 'Godkänn och skapa utkast i Bokio',
    approved: 'Utkastet är skapat i Bokio med underlaget bifogat',
    inBokio: (n: string | null) => (n ? `I Bokio som faktura ${n}` : 'I Bokio som utkast'),
    invoicedThere: 'Fakturerad i Bokio',
    markInvoiced: 'Redan fakturerad i Bokio…',
    pickInvoices: 'Vilka fakturor i Bokio täcker den här månaden? De kan vara från en annan månad.',
    noChoices: 'Ingen faktura för kunden i Bokio två månader före eller efter. Koppla kunden under Ekonomi › Kunder i Bokio om den saknas.',
    saveInvoiced: 'Markera som fakturerad',
    markedOk: 'Markerad som fakturerad',
    bokioError: (m: string) => `Bokio: ${m}`,
    stale: 'Någon annan ändrade underlaget. Det är omladdat; gör din ändring igen.',
    locked: 'Bekräftat. Öppna det igen för att ändra.',
    empty: 'Inget att fakturera denna månad än.'
  }
);

export const dayUi = (k: string | null, lang: Lang) => (k ? (lang === 'sv' ? dmSv(k.slice(0, 10)) : dm(k.slice(0, 10))) : '–');
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The basis's status in words; one marked as invoiced straight in Bokio says so instead of "Draft in Bokio" */
export const statusText = (b: Pick<BasisView, 'status' | 'pushedAt'>, lang: Lang) => (b.status === 'in_bokio' && !b.pushedAt ? TX[lang].invoicedThere : TX[lang].status[b.status]);
export const statusTone = (s: BasisStatus): 'ok' | 'warn' | 'info' | 'neutral' => (s === 'in_bokio' ? 'ok' : s === 'open' ? 'warn' : s === 'skipped' ? 'neutral' : 'info');
export const invoiceTone = (s: string): 'ok' | 'warn' | 'crit' | 'info' | 'neutral' => (s === 'paid' || s === 'overPaid' ? 'ok' : s === 'overdue' ? 'crit' : s === 'underPaid' ? 'warn' : s === 'draft' ? 'neutral' : 'info');

/** One invoice as a line: number, date, amount incl. VAT, Bokio's status. */
export function InvoiceLine(p: { i: InvoiceRow; lang: Lang; showClient?: boolean }) {
  const t = TX[p.lang];
  return (
    <li class="bill-inv">
      <span>
        {p.showClient ? <strong>{p.i.customerName ?? '–'} </strong> : null}
        {p.i.number ? `#${p.i.number}` : t.inv.draft} · {dayUi(p.i.invoiceDate, p.lang)}
        {p.i.dueDate && p.i.status !== 'paid' && p.i.status !== 'draft' ? <span class="muted"> · {t.due(dayUi(p.i.dueDate, p.lang))}</span> : null}
      </span>
      <span class="bill-amt">{kr(p.i.total)}</span>
      <Pill tone={invoiceTone(p.i.status)}>{t.inv[p.i.status] ?? p.i.status}</Pill>
    </li>
  );
}

const srcOf = (l: BasisLine, t: (typeof TX)['en']) => (l.itemKey === 'travel' || l.itemKey === 'travel_time' ? t.trip : l.source === 'manual' ? t.manual : l.projectId ? t.fromProject : l.agreementId ? t.fromAgreement : t.fromMondayDeal);

/**
 * The month's basis in a dialog: the lines with where they came from, edit while open, then confirm (producer); an admin
 * approves a confirmed one into a draft in Bokio. The admin may also approve straight from open.
 */
interface InvoiceChoice {
  id: string;
  number: string | null;
  status: string;
  invoiceDate: string | null;
  net: number;
  chosen: boolean;
}

/** The client's invoices made straight in Bokio around the month, to mark the basis as invoiced by them. */
function InvoicePicker(p: { api: Api; basisId: string; period: string; busy: boolean; onSave: (ids: string[]) => void; onCancel: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [list, setList] = useState<InvoiceChoice[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    p.api.get<{ invoices: InvoiceChoice[] }>(`/api/invoice-bases/${p.basisId}/invoices`).then(
      (r) => {
        setList(r.invoices);
        // already marked, or else the month's own invoices
        const chosen = r.invoices.filter((i) => i.chosen);
        setPicked((chosen.length ? chosen : r.invoices.filter((i) => i.invoiceDate?.startsWith(p.period))).map((i) => i.id));
      },
      (e) => setErr(msg(e))
    );
  }, [p.basisId]);
  if (err) return <p class="hint contract-warn">{err}</p>;
  if (!list) return null;
  return (
    <fieldset class="panel stack">
      <legend>{t.pickInvoices}</legend>
      {list.length ? (
        list.map((i) => (
          <label class="check" key={i.id}>
            <input type="checkbox" checked={picked.includes(i.id)} onChange={(e) => setPicked((e.target as HTMLInputElement).checked ? [...picked, i.id] : picked.filter((x) => x !== i.id))} /> {i.number ? `#${i.number}` : t.status.in_bokio} · {i.invoiceDate ? (lang === 'sv' ? dmSv(i.invoiceDate) : dm(i.invoiceDate)) : ''} · {kr(i.net)}
          </label>
        ))
      ) : (
        <p class="muted">{t.noChoices}</p>
      )}
      <span class="cluster">
        <button type="button" class="btn btn-sm btn-primary" disabled={p.busy || !picked.length} onClick={() => p.onSave(picked)}>
          {t.saveInvoiced}
        </button>
        <button type="button" class="btn btn-sm" onClick={p.onCancel}>
          {t.cancel}
        </button>
      </span>
    </fieldset>
  );
}

export function BasisDialog(p: { api: Api; basis: BasisView; canApprove: boolean; onChange: (b: BasisView) => void; onClose: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [b, setB] = useState(p.basis);
  const [lines, setLines] = useState<BasisLine[]>(p.basis.lines);
  const [note, setNote] = useState(p.basis.note ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [trip, setTrip] = useState<{ hint: TravelHint | null } | null>(null);
  const [picking, setPicking] = useState(false);
  const editable = b.status === 'open';
  const dirty = JSON.stringify(lines) !== JSON.stringify(b.lines) || note !== (b.note ?? '');
  const total = basisTotal(lines);
  const set = (i: number, patch: Partial<BasisLine>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const took = (next: BasisView) => {
    setB(next);
    setLines(next.lines);
    setNote(next.note ?? '');
    p.onChange(next);
  };
  const run = async (f: () => Promise<{ basis: BasisView }>, done?: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await f();
      took(r.basis);
      if (r.basis.bokioError) setErr(t.bokioError(r.basis.bokioError));
      else if (done) toast({ text: done });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'stale') {
        setErr(t.stale);
        const fresh = await p.api.get<{ bases: BasisView[] }>(`/api/finance/bases?month=${b.period}`).catch(() => null);
        const again = fresh?.bases.find((x) => x.id === b.id);
        if (again) took(again);
      } else setErr(msg(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () => p.api.put<{ basis: BasisView }>(`/api/invoice-bases/${b.id}`, { lines: lines.map((l) => ({ ...l, unitPrice: l.unitPrice })), note, rev: b.rev });
  const saveThen = async (next: () => Promise<{ basis: BasisView }>) => {
    if (dirty) await save();
    return next();
  };
  return (
    <Dialog
      title={t.basisTitle(b.company ? `${b.client.name} · ${b.company.name}` : b.client.name, b.monthLabel)}
      sub={editable ? t.basisSub : undefined}
      onClose={p.onClose}
      foot={
        <>
          {b.status === 'open' || b.status === 'confirmed' || b.status === 'approved' ? (
            p.canApprove ? (
              <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => void run(() => p.api.post(`/api/invoice-bases/${b.id}/skip`))}>
                {t.skip}
              </button>
            ) : null
          ) : null}
          {p.canApprove && !b.pushedAt && b.status !== 'in_bokio' && !picking ? (
            <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => setPicking(true)}>
              {t.markInvoiced}
            </button>
          ) : null}
          {b.status !== 'open' && (b.status !== 'in_bokio' || !b.pushedAt) && p.canApprove ? (
            <button type="button" class="btn" disabled={busy} onClick={() => void run(() => p.api.post(`/api/invoice-bases/${b.id}/reopen`))}>
              {t.reopen}
            </button>
          ) : null}
          {editable && dirty ? (
            <button type="button" class="btn" disabled={busy} onClick={() => void run(save)}>
              {t.saveLines}
            </button>
          ) : null}
          {editable ? (
            <button type="button" class={`btn${p.canApprove ? '' : ' btn-primary'}`} disabled={busy || !total.lines || total.unpriced > 0} onClick={() => void run(() => saveThen(() => p.api.post(`/api/invoice-bases/${b.id}/confirm`)), t.confirmed)}>
              {t.confirm}
            </button>
          ) : null}
          {p.canApprove && (b.status === 'open' || b.status === 'confirmed' || b.status === 'approved') ? (
            <button type="button" class="btn btn-primary" disabled={busy || !total.lines || total.unpriced > 0 || !b.linked || b.missing.length > 0} onClick={() => void run(() => saveThen(() => p.api.post(`/api/invoice-bases/${b.id}/approve`)), t.approved)}>
              {t.approve}
            </button>
          ) : null}
        </>
      }
    >
      <p>
        <Pill tone={statusTone(b.status)}>{statusText(b, lang)}</Pill> {b.status === 'in_bokio' ? <span class="muted">{t.inBokio(b.invoice?.number ?? null)}</span> : null}
      </p>
      {picking ? (
        <InvoicePicker
          api={p.api}
          basisId={b.id}
          period={b.period}
          busy={busy}
          onSave={(ids) => void run(() => p.api.post<{ basis: BasisView }>(`/api/invoice-bases/${b.id}/invoiced`, { invoiceIds: ids }), t.markedOk).then(() => setPicking(false))}
          onCancel={() => setPicking(false)}
        />
      ) : null}
      {!b.linked && p.canApprove ? <p class="hint">{TX[lang].notLinked}</p> : null}
      {b.elsewhere?.length ? <p class="hint contract-warn">{t.elsewhere(b.elsewhere.map((i) => (i.number ? `#${i.number}` : '–')).join(', '))}</p> : null}
      {b.missing.length ? <p class="hint">{t.missing(b.missing.map((f) => t.fields[f] ?? f).join(', '))}</p> : null}
      {editable ? <TravelHints hints={b.travel.hints} lines={lines} onPick={(hint) => setTrip({ hint })} /> : null}
      <div class="tbl-wrap">
        <table class="tbl basis-tbl">
          <thead>
            <tr>
              <th scope="col">{t.text}</th>
              <th scope="col" class="num">
                {t.qty}
              </th>
              <th scope="col" class="num">
                {t.price}
              </th>
              <th scope="col">{t.vat}</th>
              <th scope="col" class="num">
                {t.sum}
              </th>
              {editable ? <th scope="col" aria-label={t.remove} /> : null}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.key || i} class={l.excluded ? 'is-excluded' : undefined}>
                <td>
                  {editable && !l.excluded ? <input class="input input--wide" aria-label={t.text} value={l.text} onInput={(e) => set(i, { text: (e.target as HTMLInputElement).value })} /> : <span>{l.text}</span>}
                  <span class="cell-sub">
                    {srcOf(l, t)}
                    {l.edited ? `, ${t.changed}` : ''}
                  </span>
                </td>
                <td class="num">
                  {editable && !l.excluded ? (
                    <span class="basis-qty">
                      <input class="input input--num" type="number" min="0" step="0.5" aria-label={t.qty} value={l.qty} onInput={(e) => set(i, { qty: Number((e.target as HTMLInputElement).value) })} />
                      {l.source === 'manual' ? (
                        <select class="input" aria-label={t.unit} value={l.unit} onChange={(e) => set(i, { unit: (e.target as HTMLSelectElement).value as BasisLine['unit'] })}>
                          {BASIS_UNITS.map((u) => (
                            <option value={u}>{t.units[u]}</option>
                          ))}
                        </select>
                      ) : (
                        <span class="muted">{t.units[l.unit]}</span>
                      )}
                    </span>
                  ) : (
                    `${String(l.qty).replace('.', ',')} ${t.units[l.unit] ?? ''}`
                  )}
                </td>
                <td class="num">
                  {editable && !l.excluded ? (
                    <input
                      class={`input input--num${l.unitPrice === null ? ' is-invalid' : ''}`}
                      type="number"
                      step="1"
                      aria-label={t.price}
                      placeholder={t.noPrice}
                      value={l.unitPrice ?? ''}
                      onInput={(e) => {
                        const v = (e.target as HTMLInputElement).value;
                        set(i, { unitPrice: v === '' ? null : Number(v) });
                      }}
                    />
                  ) : l.unitPrice === null ? (
                    <span class="contract-warn">{t.noPrice}</span>
                  ) : (
                    kr(l.unitPrice)
                  )}
                </td>
                <td>
                  {editable && !l.excluded ? (
                    <select class="input" aria-label={t.vat} value={l.vat} onChange={(e) => set(i, { vat: Number((e.target as HTMLSelectElement).value) as BasisLine['vat'] })}>
                      {VAT_RATES.map((v) => (
                        <option value={v}>{v} %</option>
                      ))}
                    </select>
                  ) : (
                    `${l.vat} %`
                  )}
                </td>
                <td class="num">{kr(lineNet(l))}</td>
                {editable ? (
                  <td>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={() => (l.source === 'manual' ? setLines(lines.filter((_, j) => j !== i)) : set(i, { excluded: !l.excluded }))}
                    >
                      {l.excluded ? t.restore : t.remove}
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
            {!lines.length ? (
              <tr>
                <td colSpan={editable ? 6 : 5} class="muted">
                  {t.empty}
                </td>
              </tr>
            ) : null}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={4}>
                {t.total}
                <span class="cell-sub">{t.totalVat(kr(total.vat), kr(total.gross))}</span>
              </th>
              <td class="num">
                <strong>{kr(total.net)}</strong>
              </td>
              {editable ? <td /> : null}
            </tr>
          </tfoot>
        </table>
      </div>
      {editable ? (
        <p>
          <button type="button" class="btn btn-sm" onClick={() => setLines([...lines, { key: '', source: 'manual', text: '', qty: 1, unit: 'piece', unitPrice: null, vat: 25 }])}>
            {t.addLine}
          </button>{' '}
          <button type="button" class="btn btn-sm" onClick={() => setTrip({ hint: null })}>
            {tripLabel(lang)}
          </button>
          {/* priced from the client's agreement (owner 2026-10-29) */}
          {quickLines(b.costs.costs, lang).map((q) => (
            <>
              {' '}
              <button type="button" class="btn btn-sm" onClick={() => setLines([...lines, q.line])}>
                + {q.label}
              </button>
            </>
          ))}
        </p>
      ) : null}
      {trip ? <TripDialog travel={b.travel} hint={trip.hint} onAdd={(add) => setLines([...lines, ...add])} onClose={() => setTrip(null)} /> : null}
      <div class="field">
        <label class="label" for="basis-note">
          {t.note}
        </label>
        {editable ? <textarea class="input input--wide" id="basis-note" rows={2} value={note} onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)} /> : <p id="basis-note">{b.note || '–'}</p>}
      </div>
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </Dialog>
  );
}

const DETAIL_FIELDS = ['orgNo', 'vatNo', 'line1', 'line2', 'postalCode', 'city', 'country', 'email', 'paymentTerms', 'reference', 'poNumber'] as const;
type DetailKey = (typeof DETAIL_FIELDS)[number];
type Form = Record<DetailKey, string>;
const formOf = (v: BillingView): Form => Object.fromEntries(DETAIL_FIELDS.map((k) => [k, (k === 'orgNo' ? v.orgNo : v.billing[k]) ?? ''])) as Form;

function DetailsDialog(p: { api: Api; clientId: string; v: BillingView; onSaved: (v: BillingView) => void; onClose: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [f, setF] = useState<Form>(formOf(p.v));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fill = () => {
    const s = p.v.suggestion ?? {};
    setF({ ...f, ...Object.fromEntries(Object.entries(s).filter(([k, x]) => x && !f[k as DetailKey])) });
  };
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { orgNo, ...billing } = f;
      const r = await p.api.put<BillingView>(`/api/clients/${p.clientId}/billing`, { orgNo, billing });
      toast({ text: r.pushError ? t.pushFailed(r.pushError) : r.bokio.linked && r.bokio.on ? t.savedBokio : t.saved });
      p.onSaved(r);
      p.onClose();
    } catch (e) {
      setErr(msg(e));
    } finally {
      setBusy(false);
    }
  };
  const field = (k: DetailKey) => (
    <div class="field" key={k}>
      <label class="label" for={`bill-${k}`}>
        {t.fields[k]}
      </label>
      <input class="input input--wide" id={`bill-${k}`} type={k === 'email' ? 'email' : 'text'} inputMode={k === 'paymentTerms' ? 'numeric' : undefined} value={f[k]} onInput={(e) => setF({ ...f, [k]: (e.target as HTMLInputElement).value })} />
    </div>
  );
  return (
    <Dialog
      title={t.details}
      onClose={p.onClose}
      onSubmit={() => void submit()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {t.save}
          </button>
        </>
      }
    >
      {p.v.mondayText && p.v.suggestion ? (
        <div class="bill-monday">
          <p class="small muted">{t.fromMonday}</p>
          <p class="small bill-pre">{[p.v.mondayText.address, p.v.mondayText.invoiceInfo].filter(Boolean).join('\n')}</p>
          <button type="button" class="btn btn-sm" onClick={fill}>
            {t.useSuggestion}
          </button>
        </div>
      ) : null}
      <div class="cluster">
        {field('orgNo')}
        {field('vatNo')}
      </div>
      {field('line1')}
      {field('line2')}
      <div class="cluster">
        {field('postalCode')}
        {field('city')}
        {field('country')}
      </div>
      {field('email')}
      <div class="cluster">
        {field('paymentTerms')}
        {field('reference')}
        {field('poNumber')}
      </div>
      <p class="hint">{t.refHint}</p>
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </Dialog>
  );
}

/** The invoicing block on the client card: details, the Bokio link, a conflict to settle, the month's basis and the invoices. */
export function ClientBillingBlock(p: { api: Api; clientId: string }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<BillingView | null>(null);
  const [editing, setEditing] = useState(false);
  const [basisOpen, setBasisOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // the block is for producers, sales and admins; anyone else gets 403 and sees nothing
    p.api.get<BillingView>(`/api/clients/${p.clientId}/billing`).then(setV, () => setV(null));
  }, [p.clientId]);
  if (!v) return null;
  const b = v.billing;
  const address = [b.line1, b.line2, [b.postalCode, b.city].filter(Boolean).join(' '), b.country && b.country !== 'SE' ? b.country : null].filter(Boolean);
  const any = v.orgNo || address.length || b.email;
  const resolve = async (use: 'hub' | 'bokio') => {
    setBusy(true);
    try {
      setV(await p.api.post<BillingView>(`/api/clients/${p.clientId}/billing/resolve`, { use }));
    } catch (e) {
      toast({ text: msg(e) });
    } finally {
      setBusy(false);
    }
  };
  const c = v.bokio.conflict;
  return (
    <section class="blk" aria-labelledby="cc-bill">
      <div class="blk-head">
        <h2 class="blk-title" id="cc-bill">
          {t.head}
        </h2>
        {v.canEdit ? (
          <div class="blk-actions">
            <button type="button" class="btn btn-sm" onClick={() => setEditing(true)}>
              {any ? t.edit : t.add}
            </button>
          </div>
        ) : null}
      </div>
      {c ? (
        <div class="bill-conflict" role="alert">
          <strong>{t.conflict}</strong>
          <p class="small">{t.conflictBody(c.fields.map((f) => t.fields[f] ?? f).join(', '))}</p>
          <dl class="bill-diff small">
            {c.fields.map((f) => (
              <>
                <dt>{t.fields[f] ?? f}</dt>
                <dd>
                  {t.hubSide}: {(f === 'orgNo' ? v.orgNo : (b as unknown as Record<string, string | null>)[f]) || '–'} · {t.bokioSide}: {(c.bokio as unknown as Record<string, string | null>)[f] || '–'}
                </dd>
              </>
            ))}
          </dl>
          {v.canEdit ? (
            <p class="cluster">
              <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void resolve('hub')}>
                {t.keepHub}
              </button>
              <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void resolve('bokio')}>
                {t.takeBokio}
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
      {any ? (
        <dl class="bill-dl small">
          {v.orgNo ? (
            <>
              <dt>{t.fields.orgNo}</dt>
              <dd>{v.orgNo}</dd>
            </>
          ) : null}
          {address.length ? (
            <>
              <dt>{t.fields.line1}</dt>
              <dd>
                {address.map((x) => (
                  <span class="cell-sub">{x}</span>
                ))}
              </dd>
            </>
          ) : null}
          {b.email ? (
            <>
              <dt>{t.fields.email}</dt>
              <dd>{b.email}</dd>
            </>
          ) : null}
          {b.reference || b.poNumber ? (
            <>
              <dt>{t.fields.reference}</dt>
              <dd>{[b.reference, b.poNumber ? `PO ${b.poNumber}` : null].filter(Boolean).join(' · ')}</dd>
            </>
          ) : null}
        </dl>
      ) : (
        <p class="muted small">{t.noDetails}</p>
      )}
      {v.missing.length && any ? <p class="small contract-warn">{t.missing(v.missing.map((f) => t.fields[f] ?? f).join(', '))}</p> : null}
      <TravelRow api={p.api} clientId={p.clientId} v={v} canEdit={v.canEdit} onSaved={setV} />
      {v.canConfirm ? <ClientTermsBlock api={p.api} clientId={p.clientId} /> : null}
      {v.bokio.on ? <p class="small muted">{v.bokio.linked ? (v.bokio.syncedAt ? t.linkedSynced(dayUi(v.bokio.syncedAt, lang)) : t.linked) : t.notLinked}</p> : null}
      {v.bokio.companies?.length ? <p class="small muted">{t.alsoCompanies(v.bokio.companies.map((c) => c.name).join(', '))}</p> : null}
      {(v.bases ?? (v.basis ? [v.basis] : []))
        .filter((b) => b.lines.length || b.status !== 'open')
        .map((b) => (
          <div class="bill-basis">
            <p>
              <strong>{t.basis(b.monthLabel)}</strong>
              {b.company ? <span class="muted"> · {b.company.name}</span> : null} <Pill tone={statusTone(b.status)}>{t.status[b.status]}</Pill>
            </p>
            <p class="small">
              {kr(b.total.net)} {t.exVat} · {t.lines(b.total.lines)}
            </p>
            <button type="button" class="btn btn-sm" onClick={() => setBasisOpen(b.id)}>
              {t.open}
            </button>
          </div>
        ))}
      {v.canConfirm && v.bokio.on ? (
        <>
          <h3 class="bill-h3">{t.invoices}</h3>
          {v.invoices.length ? (
            <ul class="bill-invs">
              {v.invoices.map((i) => (
                <InvoiceLine i={i} lang={lang} />
              ))}
            </ul>
          ) : (
            <p class="muted small">{t.noInvoices}</p>
          )}
        </>
      ) : null}
      {editing ? <DetailsDialog api={p.api} clientId={p.clientId} v={v} onSaved={setV} onClose={() => setEditing(false)} /> : null}
      {(() => {
        const open = basisOpen ? (v.bases ?? (v.basis ? [v.basis] : [])).find((b) => b.id === basisOpen) : undefined;
        return open ? (
          <BasisDialog
            api={p.api}
            basis={open}
            canApprove={v.canApprove}
            onChange={(basis) => setV({ ...v, basis: v.basis?.id === basis.id ? basis : v.basis, bases: v.bases?.map((x) => (x.id === basis.id ? basis : x)) })}
            onClose={() => setBasisOpen(null)}
          />
        ) : null;
      })()}
    </section>
  );
}
